import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useReportState } from '../hooks/useReportState';

import { ChatContext } from './ChatContext';

interface ChatLayoutContextType {
  showLanding: boolean;
  setShowLanding: (show: boolean) => void;
  hasCompletedAnimation: boolean;
  setHasCompletedAnimation: (hasCompleted: boolean) => void;
  isLogoAnimating: boolean;
  setIsLogoAnimating: (isAnimating: boolean) => void;
  logoAnimationPhase: 'center' | 'moving-up' | 'moving-left' | 'header';
  setLogoAnimationPhase: (
    phase: 'center' | 'moving-up' | 'moving-left' | 'header'
  ) => void;
  closeAllPanels: () => void;
  setCloseAllPanels: (fn: () => void) => void;
  reportState: ReturnType<typeof useReportState>;
}

export const ChatLayoutContext = createContext<ChatLayoutContextType>({
  showLanding: true,
  setShowLanding: () => {},
  hasCompletedAnimation: false,
  setHasCompletedAnimation: () => {},
  isLogoAnimating: false,
  setIsLogoAnimating: () => {},
  logoAnimationPhase: 'center',
  setLogoAnimationPhase: () => {},
  closeAllPanels: () => {},
  setCloseAllPanels: () => {},
  reportState: {} as ReturnType<typeof useReportState>,
});

export const ChatLayoutProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { messages } = useContext(ChatContext);
  const prevPathRef = useRef<string>(location.pathname);
  const closeAllPanelsRef = useRef<(() => void) | null>(null);

  const [showLanding, setShowLanding] = useState(true);
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(false);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState<
    'center' | 'moving-up' | 'moving-left' | 'header'
  >('center');

  const reportState = useReportState();
  const { closeAllPanels: closeReportStatePanels } = reportState;

  const closeAllPanels = useCallback(() => {
    if (closeAllPanelsRef.current) {
      closeAllPanelsRef.current();
    }
    closeReportStatePanels();
  }, [closeReportStatePanels]);

  const setCloseAllPanels = useCallback((fn: () => void) => {
    closeAllPanelsRef.current = fn;
  }, []);

  const isConversationRoute = location.pathname.startsWith(
    '/chatbv/chat/conversation'
  );
  const isBaseChatRoute = location.pathname === '/chatbv/chat';

  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    if (isBaseChatRoute && prevPath === '/chatbv/chat/conversation') {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
      setIsLogoAnimating(false);
      closeAllPanels();
    }
    // If on conversation route with messages, ensure we're not showing landing
    else if (isConversationRoute && showLanding && messages.length > 0) {
      setShowLanding(false);
      setHasCompletedAnimation(true);
    }
    // If on base route, ensure landing is shown
    else if (isBaseChatRoute && !showLanding && messages.length === 0) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
      closeAllPanels();
    }
  }, [
    location.pathname,
    isConversationRoute,
    isBaseChatRoute,
    messages.length,
    showLanding,
    closeAllPanels,
  ]);

  return (
    <ChatLayoutContext.Provider
      value={{
        showLanding,
        setShowLanding,
        hasCompletedAnimation,
        setHasCompletedAnimation,
        isLogoAnimating,
        setIsLogoAnimating,
        logoAnimationPhase,
        setLogoAnimationPhase,
        closeAllPanels,
        setCloseAllPanels,
        reportState,
      }}
    >
      {children}
    </ChatLayoutContext.Provider>
  );
};
